import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface EnvelopeOpeningProps {
  onOpen: () => void;
}

const petalColors = [
  'bg-rose-400',
  'bg-rose-500',
  'bg-red-400',
  'bg-red-300',
  'bg-orange-300',
  'bg-amber-200',
  'bg-rose-300',
];

function RosePetalRain() {
  const petals = Array.from({ length: 48 }).map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 2.5,
    duration: 3 + Math.random() * 4,
    size: 8 + Math.random() * 14,
    drift: (Math.random() - 0.5) * 120,
    rotation: Math.random() * 360,
    color: petalColors[Math.floor(Math.random() * petalColors.length)],
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[60]">
      {petals.map((petal) => (
        <motion.div
          key={petal.id}
          className={`absolute ${petal.color} opacity-80`}
          style={{
            left: petal.left,
            width: petal.size,
            height: petal.size * 0.85,
            borderRadius: '60% 40% 50% 50% / 50% 40% 60% 50%',
          }}
          initial={{ y: -40, opacity: 0, rotate: petal.rotation }}
          animate={{
            y: ['0vh', '110vh'],
            x: [0, petal.drift, -petal.drift * 0.5, petal.drift * 0.8],
            rotate: [petal.rotation, petal.rotation + 180, petal.rotation + 360],
            opacity: [0, 0.85, 0.85, 0.6, 0],
          }}
          transition={{
            duration: petal.duration,
            delay: petal.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

export default function EnvelopeOpening({ onOpen }: EnvelopeOpeningProps) {
  const [stage, setStage] = useState<'closed' | 'opening' | 'open'>('closed');
  const [exiting, setExiting] = useState(false);

  const handleClick = () => {
    if (stage !== 'closed') return;
    setStage('opening');
    setTimeout(() => {
      setStage('open');
    }, 800);
  };

  const handleEnter = () => {
    setExiting(true);
    setTimeout(() => {
      onOpen();
    }, 600);
  };

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="envelope-overlay"
          className="fixed inset-0 z-[100] bg-midnight-dark flex items-center justify-center"
          exit={{ opacity: 0, scale: 1.05, filter: 'blur(8px)' }}
          transition={{ duration: 0.9, ease: 'easeInOut' }}
        >
          {/* Rose petal shower — appears when card opens */}
          <AnimatePresence>
            {stage === 'open' && <RosePetalRain key="petals" />}
          </AnimatePresence>

          {/* Subtle floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 12 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-copper/30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.2, 0.6, 0.2],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {stage === 'closed' && (
              <motion.div
                key="closed"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center gap-8"
              >
                <motion.p
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="font-sans text-xs uppercase tracking-[4px] text-pearl/40"
                >
                  You have an invitation
                </motion.p>

                <motion.div
                  onClick={handleClick}
                  className="relative cursor-pointer perspective-1000"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative w-72 md:w-96 h-44 md:h-56 bg-midnight-card rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)] border border-cream/5 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[55%] z-10">
                      <svg viewBox="0 0 384 210" className="w-full h-full" preserveAspectRatio="none">
                        <polygon points="0,0 192,160 384,0" fill="#121827" stroke="rgba(198,142,95,0.15)" strokeWidth="1" />
                      </svg>
                    </div>

                    <motion.div
                      className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-copper/15 border border-copper/30 flex items-center justify-center"
                      animate={{
                        boxShadow: [
                          '0 0 15px rgba(198,142,95,0.15)',
                          '0 0 30px rgba(198,142,95,0.3)',
                          '0 0 15px rgba(198,142,95,0.15)',
                        ],
                      }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <span className="font-display text-copper text-sm md:text-base font-bold">S</span>
                    </motion.div>

                    <div className="absolute bottom-0 left-0 right-0 h-[45%]">
                      <svg viewBox="0 0 384 172" className="w-full h-full" preserveAspectRatio="none">
                        <polygon points="0,0 192,130 384,0 384,172 0,172" fill="#0D1320" stroke="rgba(198,142,95,0.1)" strokeWidth="1" />
                      </svg>
                    </div>

                    <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l border-copper/10 rounded-bl" />
                    <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-copper/10 rounded-br" />
                  </div>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="font-sans text-xs text-pearl/30 tracking-wider"
                >
                  Tap to open
                </motion.p>
              </motion.div>
            )}

            {stage === 'opening' && (
              <motion.div
                key="opening"
                className="relative perspective-1000"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.6 }}
              >
                <div className="relative w-72 md:w-96 h-44 md:h-56">
                  <div className="absolute inset-0 bg-midnight-card rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)] border border-cream/5">
                    <svg viewBox="0 0 384 172" className="absolute bottom-0 w-full h-[45%]" preserveAspectRatio="none">
                      <polygon points="0,0 192,130 384,0 384,172 0,172" fill="#0D1320" stroke="rgba(198,142,95,0.1)" strokeWidth="1" />
                    </svg>
                  </div>

                  <motion.div
                    className="absolute top-0 left-0 right-0 h-[55%] z-10"
                    style={{ transformOrigin: 'top' }}
                    animate={{ rotateX: -180, opacity: 0.3 }}
                    transition={{ duration: 0.8, ease: 'easeInOut' }}
                  >
                    <svg viewBox="0 0 384 210" className="w-full h-full" preserveAspectRatio="none">
                      <polygon points="0,0 192,160 384,0" fill="#121827" stroke="rgba(198,142,95,0.15)" strokeWidth="1" />
                    </svg>
                  </motion.div>

                  {/* Card peeking out */}
                  <motion.div
                    className="absolute left-1/2 -translate-x-1/2"
                    initial={{ top: '50%', opacity: 0 }}
                    animate={{ top: '-60%', opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
                  >
                    <div className="w-60 md:w-72 bg-cream rounded-lg shadow-lg border border-copper/10 p-4 text-center">
                      <p className="font-sans text-[8px] uppercase tracking-[3px] text-ink/40">Wedding Invitation</p>
                    </div>
                  </motion.div>

                  {/* Wax seal falling */}
                  <motion.div
                    className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-copper flex items-center justify-center"
                    animate={{ y: 120, rotate: 45, opacity: 0 }}
                    transition={{ duration: 0.6, ease: 'easeIn' }}
                  >
                    <span className="font-display text-cream text-xs font-bold">S</span>
                  </motion.div>
                </div>
              </motion.div>
            )}

            {stage === 'open' && (
              <motion.div
                key="open"
                initial={{ opacity: 0, y: 40, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 1.08, y: -20, filter: 'blur(6px)' }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="flex flex-col items-center gap-8"
              >
                {/* Full invitation card — dark cinematic theme */}
                <div className="bg-ink-light/70 backdrop-blur-md rounded-2xl p-8 md:p-14 shadow-[0_40px_100px_rgba(0,0,0,0.5)] max-w-md mx-auto border border-copper/25 relative">
                  {/* Corner ornaments */}
                  <div className="absolute top-5 left-5 w-8 h-8 border-t-2 border-l-2 border-copper/30 rounded-tl-lg" />
                  <div className="absolute top-5 right-5 w-8 h-8 border-t-2 border-r-2 border-copper/30 rounded-tr-lg" />
                  <div className="absolute bottom-5 left-5 w-8 h-8 border-b-2 border-l-2 border-copper/30 rounded-bl-lg" />
                  <div className="absolute bottom-5 right-5 w-8 h-8 border-b-2 border-r-2 border-copper/30 rounded-br-lg" />

                  <div className="text-center relative z-10">
                    <p className="font-sans text-[10px] uppercase tracking-[4px] text-copper/50 mb-4">
                      Wedding Invitation
                    </p>

                    <h2 className="font-display text-3xl md:text-4xl text-cream font-bold mb-2 drop-shadow-lg">
                      Sharoof &amp; Adhabiya
                    </h2>

                    <p className="font-serif text-base text-pearl/60 italic mb-6">
                      June 6, 2026
                    </p>

                    <div className="flex items-center justify-center gap-3 mb-6">
                      <span className="w-12 h-px bg-copper/30" />
                      <i className="ri-heart-3-fill text-copper/50 w-4 h-4 flex items-center justify-center" />
                      <span className="w-12 h-px bg-copper/30" />
                    </div>

                    <p className="font-sans text-xs text-copper/50 uppercase tracking-wider mb-8">
                      Nikkah &middot; 11:30 AM
                    </p>

                    {/* Enter button */}
                    <motion.button
                      whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(198,142,95,0.25)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleEnter}
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-copper text-midnight-dark font-sans text-xs uppercase tracking-[2px] transition-all duration-300 hover:bg-copper-light font-semibold"
                    >
                      <span>Enter Invitation</span>
                      <motion.span
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                      </motion.span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}